// Conversion des distances pour l'affichage.
//
// Tout est stocké en MÈTRES : la base, l'API, les statistiques. L'unité n'est
// qu'une préférence d'affichage, appliquée au dernier moment — c'est ce qui
// permet à deux joueurs, l'un en mètres l'autre en yards, de comparer les
// mêmes séances sans qu'aucune conversion ne se fige en base.

export type DistanceUnit = 'm' | 'yd';

/** 1 yard = 0,9144 m, exactement (accord international de 1959). */
const M_PER_YARD = 0.9144;

export const unitLabel = (unit: DistanceUnit): string => (unit === 'yd' ? 'yd' : 'm');

export const unitName = (unit: DistanceUnit): string => (unit === 'yd' ? 'yards' : 'mètres');

/** Mètres → unité d'affichage, arrondi à l'entier. */
export function fromMetres(metres: number, unit: DistanceUnit): number {
    return unit === 'yd' ? Math.round(metres / M_PER_YARD) : Math.round(metres);
}

/** Unité d'affichage → mètres, pour l'écriture. */
export function toMetres(value: number, unit: DistanceUnit): number {
    return unit === 'yd' ? Math.round(value * M_PER_YARD) : Math.round(value);
}

/** Les panneaux du practice, dans l'unité affichée.
 *
 *  Sans télémètre, on ne mesure pas : on lit le panneau le plus proche de
 *  l'endroit où la balle est tombée. La valeur est donc connue exactement, et
 *  la faire atteindre par des incréments est un détour.
 *
 *  Liste écrite à la main et non calculée par pas régulier : les pancartes
 *  d'un practice ne sont pas à intervalle constant. Celles-ci suivent le
 *  terrain où l'application est utilisée — 20 pour les approches courtes, puis
 *  50, 70, 100, 130, et les longues distances de 25 en 25.
 *
 *  L'échelle est calculée dans l'unité LUE, jamais convertie. Un joueur en
 *  yards doit voir 100, 125, 150 — pas 109, 137, 164, qui sont les panneaux
 *  métriques traduits et ne correspondent à aucune pancarte.
 *
 *  Huit valeurs de part et d'autre : la grille en compte quatre par rang, et
 *  un neuvième panneau ouvrirait un troisième rang à une seule case. */
export function panelMarks(unit: DistanceUnit): number[] {
    return unit === 'yd'
        ? [25, 50, 75, 100, 125, 150, 175, 200]
        : [20, 50, 70, 100, 130, 150, 175, 200];
}

/** Distance formatée, unité comprise. `null` reste `null` : une distance non
 *  mesurée ne doit pas s'afficher comme un zéro. */
export function formatDistance(
    metres: number | null | undefined,
    unit: DistanceUnit,
    { withUnit = true }: { withUnit?: boolean } = {},
): string | null {
    if (metres == null) return null;
    const v = fromMetres(metres, unit);
    return withUnit ? `${v} ${unitLabel(unit)}` : String(v);
}
